import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@utils/cn';

export interface ButtonLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to?: string; // For internal routing
  href?: string; // For external links
  external?: boolean; // Whether the link should open in a new tab
  className?: string;
  onClick?: () => void;
}

export const ButtonLink = React.forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  (
    { children, className, to, href, external = false, onClick, ...props },
    ref,
  ) => {
    const baseStyles = [
      'text-indigo-600 dark:text-indigo-400 text-sm',
      'hover:text-indigo-800 dark:hover:text-indigo-300',
      'underline',
      'transition-colors duration-200',
      'cursor:pointer',
    ];

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (onClick) {
        onClick();
      }

      // If it's an external link, let the default behavior handle it
      if (external || href) {
        return;
      }

      // For internal routing with 'to' prop, let Link handle it
      if (to) {
        return;
      }

      // If no href, to, or onClick, prevent default
      if (!href && !to && !onClick) {
        e.preventDefault();
      }
    };

    const linkProps = {
      className: cn(className, baseStyles),
      onClick: handleClick,
      ref,
      ...(external && { target: '_blank', rel: 'noopener noreferrer' }),
      ...props,
    };

    // If we have a 'to' prop, use React Router's Link
    if (to) {
      return (
        <Link to={to} {...linkProps}>
          {children}
        </Link>
      );
    }

    // If we have an href, use a regular anchor tag
    if (href) {
      return (
        <a href={href} {...linkProps}>
          {children}
        </a>
      );
    }

    // If we only have onClick, use a button styled like a link
    return (
      <button
        type="button"
        className={cn(className, baseStyles, 'bg-transparent border-none p-0')}
        onClick={onClick}>
        {children}
      </button>
    );
  },
);

ButtonLink.displayName = 'ButtonLink';
